import { create } from 'zustand'
import { isLocal } from '@/constants/appConfig'
import { AUTH_STORAGE_KEYS, STORAGE_TO_STATE_MAP } from './authStoreConstants'
import type { VerifyOtpEmailUser } from '../entities/otp/types'

interface AuthState {
  token: string | null
  userId: string | null
  email: string | null
  login: (token: string, userId: string, user?: VerifyOtpEmailUser | null) => void
  logout: () => void
}

// Helper to safely access localStorage (handles SSR/test environments)
const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null)

function getStoredToken(): string | null {
  const storage = getLocalStorage()
  return storage?.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN) ?? null
}

function getStoredUserId(): string | null {
  const storage = getLocalStorage()
  const userStr = storage?.getItem(AUTH_STORAGE_KEYS.USER)
  if (!userStr) return null
  try {
    const user: VerifyOtpEmailUser = JSON.parse(userStr)
    return user.id ?? null
  } catch {
    return null
  }
}

function getStoredEmail(): string | null {
  const storage = getLocalStorage()
  const userStr = storage?.getItem(AUTH_STORAGE_KEYS.USER)
  if (!userStr) return null
  try {
    const user: VerifyOtpEmailUser = JSON.parse(userStr)
    return user.email ?? null
  } catch {
    return null
  }
}

export const useAuthStore = create<AuthState>((set) => {
  // Initialize state from localStorage
  const initialToken = getStoredToken()
  const initialUserId = getStoredUserId()
  const initialEmail = getStoredEmail()

  if (typeof window !== 'undefined') {
    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key in STORAGE_TO_STATE_MAP) {
        const stateKey = STORAGE_TO_STATE_MAP[event.key as keyof typeof STORAGE_TO_STATE_MAP]
        set({ [stateKey]: event.newValue })
      }
      // Handle user object changes to update email and userId
      if (event.key === AUTH_STORAGE_KEYS.USER) {
        try {
          const user: VerifyOtpEmailUser | null = event.newValue ? JSON.parse(event.newValue) : null
          set({ email: user?.email ?? null, userId: user?.id ?? null })
        } catch {
          set({ email: null, userId: null })
        }
      }
    }
    window.addEventListener('storage', handleStorage)
  }

  return {
    token: initialToken,
    userId: initialUserId,
    email: initialEmail,
    login: (newToken: string, newUserId: string, user?: VerifyOtpEmailUser | null) => {
      const storage = getLocalStorage()
      if (storage) {
        storage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, newToken)
        if (isLocal) {
          storage.setItem(AUTH_STORAGE_KEYS.USER_ID, newUserId)
        }
        if (user) {
          storage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user))
        }

        // Prefer the passed user (avoids race); fallback to storage.
        const userEmail = user?.email ?? getStoredEmail()

        // Update Zustand state in THIS tab immediately
        // Other tabs will sync via 'storage' event listener above
        set({
          token: newToken,
          userId: newUserId,
          email: userEmail,
        })
      } else {
        set({
          token: newToken,
          userId: newUserId,
          email: null,
        })
      }
    },
    logout: () => {
      const storage = getLocalStorage()
      if (storage) {
        storage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
        storage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
        storage.removeItem(AUTH_STORAGE_KEYS.USER_ID)
        storage.removeItem(AUTH_STORAGE_KEYS.USER)
      }
      set({
        token: null,
        userId: null,
        email: null,
      })
    },
  }
})
